import { FolderContent } from '@/api/generatedApi';
import { GET_ASSET_QUERY_KEY } from '@/hooks/api/assets/useGetAsset';
import { LIST_ASSETS_BY_STATE_QUERY_KEY } from '@/hooks/api/assets/useFetchAssetsByState';
import { ASSET_STATE_COUNTS_QUERY_KEY } from '@/hooks/api/assets/useGetAssetStateCounts';
import { LIST_FOLDER_CONTENTS_QUERY_KEY } from '@/hooks/api/folders/useFetchFolderContents';
import { AssetsPageObjectModel } from '@/poms/assetsPageObjectModel';
import { api } from '@/services/api';
import { useTenants } from '@/services/tenants/useTenants';
import { isAsset } from '@/utils/assets';
import { Typography } from '@mui/material';
import { useQueryClient } from '@tanstack/react-query';
import { BulkModal } from './BulkModal';

export const BulkReprocessModal = ({
  open,
  onClose,
  selectedContent,
  onReprocessComplete,
}: {
  open: boolean;
  onClose: () => void;
  selectedContent: FolderContent[];
  onReprocessComplete?: () => void;
}) => {
  const { getTenantId } = useTenants();
  const queryClient = useQueryClient();

  const assets = selectedContent.filter(content => isAsset(content));
  const folderCount = selectedContent.filter(content => !isAsset(content)).length;

  const reprocessAsset = async (asset: FolderContent) => {
    await api.assets.reprocessAsset(getTenantId(), asset.id);
  };

  const handleComplete = () => {
    queryClient.resetQueries({ queryKey: [GET_ASSET_QUERY_KEY], exact: false });
    queryClient.resetQueries({ queryKey: [LIST_FOLDER_CONTENTS_QUERY_KEY], exact: false });
    queryClient.resetQueries({ queryKey: [LIST_ASSETS_BY_STATE_QUERY_KEY], exact: false });
    queryClient.resetQueries({ queryKey: [ASSET_STATE_COUNTS_QUERY_KEY], exact: false });
    onReprocessComplete?.();
  };

  return (
    <BulkModal
      open={open}
      onClose={onClose}
      selectedItems={assets}
      onComplete={handleComplete}
      performOperation={reprocessAsset}
      labels={{
        title: 'Reprocess assets',
        action: 'reprocess',
        actionPastTense: 'Queued for reprocessing',
        actionPresent: 'reprocessing',
        confirmButton: 'Reprocess',
        itemSingular: 'asset',
        itemPlural: 'assets',
        description: count => {
          const subject = count === 1 ? 'This asset' : `These ${count} assets`;
          return `${subject} will be queued for reprocessing.`;
        },
      }}
      buttonColor='primary'
      dataTestId={AssetsPageObjectModel.BulkReprocessModal}
      actionsStart={
        folderCount > 0 ? (
          <Typography variant='caption' color='text.secondary'>
            {folderCount} {folderCount === 1 ? 'folder' : 'folders'} cannot be reprocessed.
          </Typography>
        ) : undefined
      }
    />
  );
};
